import { z } from "zod";

const decimalIntegerSchema = z.string().regex(/^-?\d+$/);
const nonNegativeIntegerSchema = z.string().regex(/^\d+$/);
const resourceStateSchema = z.enum([
  "unknown",
  "healthy",
  "low",
  "exhausted",
]);

const resourceMetricFields = {
  available_quantity: decimalIntegerSchema.nullable(),
  remaining_basis_points: z.number().int().min(0).max(10_000).nullable(),
  revision: nonNegativeIntegerSchema.nullable(),
  state: resourceStateSchema,
  total_quantity: nonNegativeIntegerSchema.nullable(),
} as const;

const aiCreditWireSchema = z
  .object({
    ...resourceMetricFields,
    refreshes_at: z.string().datetime({ offset: true }).nullable(),
    unit: z.literal("credit"),
  })
  .strict()
  .superRefine(validateMetricConsistency);

const cloudStorageWireSchema = z
  .object({
    ...resourceMetricFields,
    unit: z.literal("byte"),
  })
  .strict()
  .superRefine(validateMetricConsistency);

export const resourceAvailabilityResponseSchema = z
  .object({
    ai_credit: aiCreditWireSchema,
    calculated_at: z.string().datetime({ offset: true }),
    cloud_storage: cloudStorageWireSchema,
    organization_id: z.string().min(1),
    schema: z.literal("resource_availability.v1"),
  })
  .strict();

export type ResourceAvailabilityWire = z.infer<
  typeof resourceAvailabilityResponseSchema
>;

export interface ResourceAvailabilityMetric {
  availableQuantity: string | null;
  remainingBasisPoints: number | null;
  revision: string | null;
  state: z.infer<typeof resourceStateSchema>;
  totalQuantity: string | null;
  unit: "byte" | "credit";
}

export interface ResourceAvailability {
  aiCredit: ResourceAvailabilityMetric & { refreshesAt: string | null };
  calculatedAt: string;
  cloudStorage: ResourceAvailabilityMetric;
  organizationId: string;
  schema: "resource_availability.v1";
}

export function parseResourceAvailability(value: unknown): ResourceAvailability {
  const wire = resourceAvailabilityResponseSchema.parse(value);
  return {
    aiCredit: {
      availableQuantity: wire.ai_credit.available_quantity,
      refreshesAt: wire.ai_credit.refreshes_at,
      remainingBasisPoints: wire.ai_credit.remaining_basis_points,
      revision: wire.ai_credit.revision,
      state: wire.ai_credit.state,
      totalQuantity: wire.ai_credit.total_quantity,
      unit: wire.ai_credit.unit,
    },
    calculatedAt: wire.calculated_at,
    cloudStorage: {
      availableQuantity: wire.cloud_storage.available_quantity,
      remainingBasisPoints: wire.cloud_storage.remaining_basis_points,
      revision: wire.cloud_storage.revision,
      state: wire.cloud_storage.state,
      totalQuantity: wire.cloud_storage.total_quantity,
      unit: wire.cloud_storage.unit,
    },
    organizationId: wire.organization_id,
    schema: wire.schema,
  };
}

function validateMetricConsistency(
  metric: {
    available_quantity: string | null;
    remaining_basis_points: number | null;
    revision: string | null;
    state: z.infer<typeof resourceStateSchema>;
    total_quantity: string | null;
  },
  context: z.RefinementCtx,
) {
  if (metric.state === "unknown") {
    for (const key of [
      "available_quantity",
      "remaining_basis_points",
      "revision",
      "total_quantity",
    ] as const) {
      if (metric[key] !== null) {
        context.addIssue({
          code: "custom",
          message: "unknown 状态不能携带伪造的数量、比例或 revision",
          path: [key],
        });
      }
    }
    return;
  }

  if (metric.available_quantity === null || metric.revision === null) {
    context.addIssue({
      code: "custom",
      message: "已知状态必须携带真实可用量和 revision",
      path: [metric.available_quantity === null ? "available_quantity" : "revision"],
    });
  }
}
